import { LicenseTypeQuestionsForm } from "@/components/registrant/license-form/license-type-questions";
import LoadingScreen from "@/components/ui/loading-screen";
import { UserType } from "@/constants";
import type { NonRegistrantRegistrantAccountListItemSchema } from "@/hera-api/client/types.gen";
import { useLicenseFormData } from "@/hooks/use-license-form-data";
import type { LicenseAnswers } from "@/types/license";

interface UnregisteredRegistrationQuestionsProps {
  licenseType: { id: string; name: string; effective_date_label?: string };
  jurisdictionId: string;
  initialAnswers?: Partial<LicenseAnswers>;
  onAnswersChange?: (answers: LicenseAnswers) => void;
  onPrevious?: () => Promise<void>;
  onNext?: (answers: LicenseAnswers) => Promise<void>;
  stepNumber?: number;
  footerActions?: "default" | "none";
  submitLabel?: string;
  userType?: UserType;
  registrantAccount?: NonRegistrantRegistrantAccountListItemSchema | null;
}

export function UnregisteredRegistrationQuestions({
  licenseType,
  jurisdictionId,
  initialAnswers,
  onAnswersChange,
  onPrevious,
  onNext,
  stepNumber = 1,
  footerActions = "none",
  submitLabel,
  userType = UserType.OPERATOR,
  registrantAccount,
}: UnregisteredRegistrationQuestionsProps) {
  const { questionsData, isLoading } = useLicenseFormData({
    licenseTypeId: licenseType.id,
    jurisdictionId,
    includeFees: false,
  });

  if (isLoading || !questionsData) {
    return <LoadingScreen message="Loading registration questions..." />;
  }

  const registrationQuestions = questionsData.questions.filter(
    (question) =>
      question.question_type === "registration" &&
      !(question.answer_type === "information" && question.display_up_front),
  );
  if (registrationQuestions.length === 0) {
    return null;
  }

  return (
    <LicenseTypeQuestionsForm
      licenseType={{
        id: licenseType.id,
        name: licenseType.name,
        requires_property: true,
        effective_date_label:
          licenseType.effective_date_label ?? "Effective date",
        effective_end_date_label: "Effective end date",
        under_development: false,
      }}
      questionsData={questionsData}
      initialAnswers={initialAnswers}
      onAnswersChange={onAnswersChange}
      onPrevious={onPrevious}
      onNext={onNext ?? (async () => undefined)}
      stepNumber={stepNumber}
      isRenewal={false}
      footerActions={footerActions}
      allQuestionsOptional
      includeEffectiveDateQuestion={false}
      submitLabel={submitLabel}
      cardTitle={
        footerActions === "none"
          ? "Registration questions (optional)"
          : `Step ${stepNumber}: Registration questions (optional)`
      }
      userType={userType}
      accountData={registrantAccount ?? undefined}
    />
  );
}

export function hasRegistrationQuestions(
  questions:
    | {
        question_type: string;
        answer_type: string;
        display_up_front?: boolean | null;
      }[]
    | undefined,
): boolean {
  return (questions ?? []).some(
    (question) =>
      question.question_type === "registration" &&
      !(question.answer_type === "information" && question.display_up_front),
  );
}
